import { FC, useEffect, useState } from 'react';
import { CustomButton, CustomInput, CustomModal, CustomSwitch, CustomTextArea, If } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import { FileUpload } from 'components';
import { CalendarIcon, CategoriesTabIcon, CustomersTabIcon, UploadIcon } from 'assets/icons';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { useUploadExpenseReceiptMutation } from '../../api';
import FormCloseReactIcon from 'assets/react-icons/FormCloseReactIcon';
import { ExpenseTabs } from '../constants';
import DayPicker from 'components/day-picker/DayPicker';
import { ListDropDown } from 'components';
import { ExpenseCategoriesOptions } from 'containers/jobs/constant';
import { Assignee, CreateJobExpenseRequest, JobExpense } from 'containers/jobs/jobs.model';

interface AddExpenseModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (expenseData: CreateJobExpenseRequest) => void;
  membersList: Assignee[];
  isEdit?: boolean;
  selectedExpense?: JobExpense;
  jobId: string;
  disableEdit?: boolean;
  handleEditReceipt?: (receiptUrl: string) => void;
  isLoading?: boolean;
}

export interface ExpenseData {
  title: string;
  amount: string;
  date: Date;
  category: string;
  reimbursableTo: Assignee;
  description: string;
  receiptUrl?: string;
  receiptPreview?: string;
  expenseType: ExpenseTabs;
}

const initialExpenseData: ExpenseData = {
  title: '',
  amount: '',
  date: new Date(),
  category: null,
  reimbursableTo: null,
  description: '',
  receiptUrl: '',
  receiptPreview: '',
  expenseType: ExpenseTabs.TEAM
};

const AddExpenseModal: FC<AddExpenseModalProps> = ({
  open,
  onClose,
  onSave,
  membersList,
  isEdit,
  selectedExpense,
  jobId,
  disableEdit = false,
  handleEditReceipt,
  isLoading = false
}) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const [uploadExpenseReceipt, { isLoading: isUploadingReceipt }] = useUploadExpenseReceiptMutation();
  const [expenseData, setExpenseData] = useState<ExpenseData>(initialExpenseData);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);

  useEffect(() => {
    if (isEdit && selectedExpense) {
      setExpenseData({
        title: selectedExpense.title,
        amount: selectedExpense.amount,
        date: new Date(selectedExpense.expenseDate),
        category: selectedExpense.category,
        reimbursableTo: membersList.find(member => member.id === selectedExpense.reimburseTo),
        description: selectedExpense.description,
        expenseType: selectedExpense.type,
        receiptUrl: selectedExpense.receiptKey,
        receiptPreview: selectedExpense.receiptUrl
      });
      setReceiptPreview(selectedExpense.receiptUrl);
    }
  }, [isEdit, selectedExpense]);

  const handleInputChange = (field: keyof ExpenseData, value: string | Date) => {
    setExpenseData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAmountChange = (value: string) => {
    // Only allow numbers and decimal point
    const formattedValue = value.replace(/[^0-9.]/g, '');
    handleInputChange('amount', formattedValue);
  };

  const handleSave = async () => {
    let payload: CreateJobExpenseRequest = {
      title: expenseData.title.trim(),
      amount: expenseData.amount.trim(),
      category: expenseData.category?.trim(),
      description: expenseData.description.trim(),
      type: expenseData.expenseType,
      expenseDate: expenseData.date.toISOString(),
      reimburseTo: expenseData.expenseType === ExpenseTabs.TEAM ? expenseData.reimbursableTo.id : null,
      receiptUrl: expenseData.receiptUrl || null
    };
    if (receiptFile) {
      try {
        const response = await uploadExpenseReceipt({
          teamId: currentTeam.id,
          jobId,
          file: receiptFile
        }).unwrap();

        payload = {
          ...payload,
          receiptUrl: response.data?.imageUrl || ''
        };
        onSave(payload);
        if (isEdit && handleEditReceipt) {
          handleEditReceipt(response.data?.imageUrl || '');
        }
      } catch (error) {
        // Error handling is done by RTK Query
      }
    } else {
      onSave(payload);
    }

    resetForm();
  };

  const resetForm = () => {
    setExpenseData(initialExpenseData);
    setReceiptFile(null);
    setReceiptPreview(null);
    onClose();
  };

  const handleRemovePreview = () => {
    setReceiptPreview(null);
    setExpenseData(prev => ({
      ...prev,
      receiptUrl: null
    }));
  };

  const isFormValid = () => {
    const hasRequiredFields =
      expenseData.title?.trim() !== '' &&
      expenseData.category &&
      expenseData.category?.trim() !== '' &&
      expenseData.amount?.trim() !== '';

    return expenseData.expenseType === ExpenseTabs.TEAM
      ? hasRequiredFields && expenseData.reimbursableTo
      : hasRequiredFields;
  };

  return (
    <CustomModal open={open} width="md:w-full max-w-[914px]">
      <div className="py-6 px-[30px]">
        <div className="flex items-center justify-between pb-6">
          <div className="text-xl font-bold text-heading">{isEdit ? 'Edit expense' : 'Record new expense'}</div>
          <FormCloseReactIcon
            id="add-expense-modal-close"
            height="24"
            width="24"
            className="cursor-pointer"
            onClick={onClose}
          />
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div className="col-span-1 grid gap-4">
            <div>
              <label className="mb-2 block text-sbase font-semibold text-primaryText">Title*</label>
              <CustomInput
                id="expense-title"
                name="title"
                placeholder="Enter title"
                value={expenseData.title}
                onChange={value => handleInputChange('title', value)}
                className="h-[48px] w-full"
                disabled={disableEdit}
              />
            </div>

            <div>
              <label className="mb-2 block text-sbase font-semibold text-primaryText">Amount*</label>
              <CustomInput
                id="expense-amount"
                name="amount"
                placeholder="0.00"
                value={expenseData.amount}
                onChange={handleAmountChange}
                className="h-[48px] w-full pl-7"
                prefixWrapperStyle="left-3"
                prefix="$"
                disabled={disableEdit}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-2 block text-sbase font-semibold text-primaryText">Category*</label>
                <ListDropDown
                  disabled={disableEdit}
                  id="expense-category"
                  items={ExpenseCategoriesOptions}
                  selectedItem={expenseData.category ? { id: expenseData.category, name: expenseData.category } : null}
                  setSelectedItem={item => handleInputChange('category', item.id)}
                  buttonStyles="bg-secondaryBtn h-[48px] text-sbase font-semibold text-primaryText 
                    cursor-pointer px-3 py-2"
                  placeholder="Choose category"
                  spanStyles={`font-semibold text-sbase ${
                    expenseData.category ? 'text-primaryText' : 'text-accentText opacity-70'
                  }`}
                  itemStyle="font-normal text-sbase tracking-[-0.3px] leading-[28px]"
                  prefixIcon={<CategoriesTabIcon className="path-fill-current h-[18px] w-[18px] text-accentText" />}
                />
              </div>

              <div className="z-20">
                <label className="mb-2 block text-sbase font-semibold text-primaryText">Date</label>
                <DayPicker
                  disabled={disableEdit}
                  prefixIcon={<CalendarIcon className="path-fill-current h-[18px] w-[18px] text-accentText" />}
                  date={expenseData.date}
                  onSelect={date => handleInputChange('date', date)}
                  labelClassName="font-semibold text-sbase text-primaryText"
                  showPastDates={true}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="mb-2 block text-sbase font-semibold text-primaryText">
                  Reimburse to
                  {expenseData.expenseType === ExpenseTabs.TEAM && '*'}
                </label>
                <CustomSwitch
                  disabled={disableEdit}
                  id="expense-reimburse-to-switch"
                  enabled={expenseData.expenseType === ExpenseTabs.TEAM}
                  toggleSwitch={() =>
                    handleInputChange(
                      'expenseType',
                      expenseData.expenseType === ExpenseTabs.COMPANY ? ExpenseTabs.TEAM : ExpenseTabs.COMPANY
                    )
                  }
                />
              </div>
              <If condition={expenseData.expenseType === ExpenseTabs.TEAM}>
                <ListDropDown
                  disabled={disableEdit}
                  id="expense-reimburse-to"
                  items={membersList}
                  selectedItem={expenseData.reimbursableTo}
                  setSelectedItem={item => handleInputChange('reimbursableTo', item)}
                  buttonStyles={`bg-secondaryBtn h-[48px] text-sbase 
                font-semibold text-primaryText cursor-pointer px-3 py-2 
                ${expenseData.expenseType === ExpenseTabs.COMPANY ? 'pointer-events-none opacity-70' : ''}`}
                  placeholder="Choose reimbursable to"
                  spanStyles={`font-semibold text-sbase leading-[21px] ${
                    expenseData.reimbursableTo ? 'text-primaryText' : 'text-accentText opacity-70'
                  }`}
                  itemStyle="font-normal text-sbase tracking-[-0.3px] leading-[28px]"
                  prefixIcon={<CustomersTabIcon width={22} height={22} />}
                />
              </If>
            </div>
            <div>
              <label className="mb-2 block text-sbase font-semibold text-primaryText">Description</label>
              <CustomTextArea
                disabled={disableEdit}
                id="expense-description"
                name="description"
                placeholder="Enter expense description"
                value={expenseData.description}
                onChange={value => handleInputChange('description', value)}
                className="h-[80px] w-full"
              />
            </div>
          </div>

          <div className="col-span-1">
            <label className="mb-2 block text-sbase font-semibold text-primaryText">Receipt</label>
            <FileUpload
              id="expense-receipt-upload"
              isUploadInProgress={isUploadingReceipt}
              prefill={false}
              setFile={setReceiptFile}
              filePreview={receiptPreview}
              setPreview={setReceiptPreview}
              className="h-[calc(100%-1.75rem)] border-borderGray bg-tableHeaderBackground"
              FileUploadIcon={UploadIcon}
              supportedFileTypes={{
                'image/jpeg': [],
                'image/png': [],
                'image/webp': []
              }}
              allowMultiple={false}
              removePreview={handleRemovePreview}
              description={
                <span>
                  Drag and drop your receipt here <span className="mt-2.5 block">or click to upload image</span>
                </span>
              }
              subText={null}
              icon={'RED_TRASH_ICON'}
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <CustomButton
            id="cancel-expense-button"
            type={ButtonType.SECONDARY}
            className="h-[38px] px-5"
            onClick={onClose}>
            Cancel
          </CustomButton>
          <CustomButton
            isLoading={isLoading}
            id="save-expense-button"
            className="h-[38px] px-5"
            disabled={!isFormValid()}
            onClick={handleSave}>
            Save
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default AddExpenseModal;
